import { useEffect, useRef } from "react";

const etapas = [
  {
    num: "01",
    title: "Diagnóstico",
    desc: "Mapeamos a situação atual da empresa: contratos vigentes, custos, perfil dos colaboradores e necessidades reais.",
    icon: "ri-search-eye-line",
  },
  {
    num: "02",
    title: "Planejamento",
    desc: "Desenvolvemos uma estratégia personalizada com foco em custo-benefício, cobertura adequada e alinhamento com a cultura da empresa.",
    icon: "ri-draft-line",
  },
  {
    num: "03",
    title: "Comparação de opções",
    desc: "Apresentamos as melhores alternativas do mercado com análise técnica, transparente e sem conflito de interesse.",
    icon: "ri-bar-chart-grouped-line",
  },
  {
    num: "04",
    title: "Implantação",
    desc: "Gerenciamos todo o processo de contratação, migração e comunicação com os colaboradores para uma transição sem fricção.",
    icon: "ri-rocket-2-line",
  },
  {
    num: "05",
    title: "Acompanhamento contínuo",
    desc: "Monitoramos indicadores, revisamos contratos e estamos sempre disponíveis para suporte estratégico e operacional.",
    icon: "ri-loop-right-line",
  },
];

export default function Solucao() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".sol-item").forEach((item, i) => {
              setTimeout(() => item.classList.add("sol-visible"), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="solucao" className="py-16 lg:py-20 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div className="lg:sticky lg:top-28">
            <div className="inline-flex items-center gap-2 mb-5">
              <span className="w-6 h-px bg-black" />
              <span className="text-black text-xs font-semibold tracking-widest uppercase">
                Nossa abordagem
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-black leading-tight mb-6">
              Uma solução completa, do diagnóstico ao resultado
            </h2>
            <p className="text-black/70 text-lg leading-relaxed mb-8">
              Não vendemos produtos. Construímos estratégias. Cada empresa recebe um plano sob medida, com acompanhamento real e resultados mensuráveis.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => document.querySelector("#contato")?.scrollIntoView({ behavior: "smooth" })}
                className="whitespace-nowrap bg-black text-white font-semibold px-7 py-3.5 rounded-full hover:bg-gray-900 transition-all duration-200 cursor-pointer text-sm"
              >
                Solicitar diagnóstico
              </button>
            </div>

            {/* Visual accent */}
            <div className="mt-12 p-6 bg-[#FBC52D]/8 border border-[#FBC52D]/20 rounded-2xl">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#FBC52D] shrink-0">
                  <i className="ri-shield-check-line text-black text-lg" />
                </div>
                <div>
                  <p className="font-semibold text-black text-sm mb-1">Sem conflito de interesse</p>
                  <p className="text-black/70 text-sm leading-relaxed">
                    Nossa recomendação é sempre baseada no que é melhor para a sua empresa, não na comissão mais alta.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Steps */}
          <div className="flex flex-col gap-0">
            {etapas.map((etapa, idx) => (
              <div
                key={etapa.num}
                className="sol-item opacity-0 translate-x-6 transition-all duration-500 flex gap-5 group"
              >
                {/* Timeline line */}
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#FBC52D]/10 border-2 border-[#FBC52D]/30 group-hover:bg-[#FBC52D] group-hover:border-[#FBC52D] transition-all duration-300 shrink-0">
                    <i className={`${etapa.icon} text-[#FBC52D] text-base group-hover:text-black transition-colors duration-300`} />
                  </div>
                  {idx < etapas.length - 1 && (
                    <div className="w-px flex-1 bg-gray-100 my-2 min-h-8" />
                  )}
                </div>

                {/* Content */}
                <div className={`pb-8 ${idx === etapas.length - 1 ? "pb-0" : ""}`}>
                  <span className="text-[#FBC52D] text-xs font-bold tracking-widest">{etapa.num}</span>
                  <h3 className="text-black font-bold text-lg mt-1 mb-2">{etapa.title}</h3>
                  <p className="text-black/70 text-sm leading-relaxed">{etapa.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        .sol-visible {
          opacity: 1 !important;
          transform: translateX(0) !important;
        }
      `}</style>
    </section>
  );
}